import { Building2, Loader2 } from 'lucide-react';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import {
  INSTITUTE_TYPE_CODE_MAX_LENGTH,
  INSTITUTE_TYPE_NAME_MAX_LENGTH,
} from '../constants/instituteType.constants';
import { useInstituteTypeForm } from '../hooks/useInstituteTypeForm';
import type { InstituteType } from '../types/instituteType.types';

interface InstituteTypeFormModalProps {
  instituteType: InstituteType | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InstituteTypeFormModal({
  instituteType,
  open,
  onOpenChange,
}: InstituteTypeFormModalProps) {
  const {
    form,
    isEditMode,
    isPending,
    submit,
    isDiscardDialogOpen,
    requestClose,
    confirmDiscard,
    cancelDiscard,
  } = useInstituteTypeForm({
    instituteType,
    open,
    onClose: () => onOpenChange(false),
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      requestClose();
      return;
    }
    onOpenChange(nextOpen);
  }

  const title = isEditMode ? 'Edit Institute Type' : 'Create Institute Type';
  const submitLabel = isEditMode ? 'Update' : 'Create';
  const submittingLabel = isEditMode ? 'Updating...' : 'Creating...';

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="gap-0 sm:max-w-lg">
          <DialogHeader className="-mx-4 -mt-4 flex-row items-center gap-3 border-b p-4 pr-12">
            <div
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-md',
                ACCENT_MARK_CLASS,
              )}
            >
              <Building2 className="size-4" />
            </div>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-6 pt-5">
              <div className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel className="gap-0.5">
                          Name<span className="text-destructive">*</span>
                        </FormLabel>
                        <CharacterCounter
                          current={field.value.length}
                          max={INSTITUTE_TYPE_NAME_MAX_LENGTH}
                        />
                      </div>
                      <FormControl>
                        <Input {...field} placeholder="Government School" autoComplete="off" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>
                          Code
                          <span className="font-normal text-muted-foreground">(optional)</span>
                        </FormLabel>
                        <CharacterCounter
                          current={field.value.length}
                          max={INSTITUTE_TYPE_CODE_MAX_LENGTH}
                        />
                      </div>
                      <FormControl>
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                          placeholder="GOVT_SCHOOL"
                          autoComplete="off"
                          className="uppercase"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex flex-col divide-y rounded-lg border">
                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="flex-row items-center justify-between gap-4 px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm leading-none font-medium">Active</span>
                        <FormDescription>Show this type when adding an institute.</FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          aria-label="Active"
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="requiresEiin"
                  render={({ field }) => (
                    <FormItem className="flex-row items-center justify-between gap-4 px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm leading-none font-medium">EIIN applicable</span>
                        <FormDescription>
                          Institutes of this type must have an EIIN number.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          aria-label="EIIN applicable"
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={requestClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      {submittingLabel}
                    </>
                  ) : (
                    submitLabel
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <DiscardChangesDialog
        open={isDiscardDialogOpen}
        onOpenChange={(nextOpen) => !nextOpen && cancelDiscard()}
        onDiscard={confirmDiscard}
      />
    </>
  );
}
